import { makeStyles, TextField } from "@material-ui/core";
import React, { useEffect, useMemo } from "react";
import { buildMenuItems, convertNullOrUnreadyToEmptyString } from "./helpers";
import { ItemType, ItemIdType, SelectProps } from "../types";

let uniqueId = 0;

const useStyles = makeStyles((theme) => ({
  readOnlyInput: {},
}));

export const SelectSingle: React.FC<SelectProps> = (props) => {
  const { lookupTracker, ...coreProps } = props;
  const { value, id, children, onChange, InputProps, name } = coreProps;
  const classes = useStyles();

  // to stop errors if the value is known but the items have not loaded yet
  //  (Material UI moans about value "foo" being applied to a select with no
  //  item that has value="foo", but is happy to get given "" until items load)
  const safeValue = convertNullOrUnreadyToEmptyString(value, !!children.length);
  const knownId = useMemo(() => id || `selectSingle_${++uniqueId}`, [id]);
  const menuItems = buildMenuItems(knownId, children, safeValue);

  const resolveValue = (value: ItemIdType | undefined) => {
    if (value === undefined) {
      return null;
    }

    if (value === "true" || value === "false") {
      const foundOriginalBooleanChild = children.find((child) => {
        const childAsItem = child as ItemType;
        return String(childAsItem.id) === String(value);
      }) as ItemType;

      return foundOriginalBooleanChild.id;
    }

    return value;
  };

  const localOnChange: typeof onChange = (event) => {
    if (onChange) {
      onChange(({
        ...event,
        target: {
          ...event.target,
          value: resolveValue(event.target.value),
        },
      } as unknown) as React.ChangeEvent<HTMLInputElement>);
    }
  };
  const readOnly = InputProps?.readOnly || props.readOnly;
  const inputClassName = readOnly ? classes.readOnlyInput : undefined;
  const inputProps =
    readOnly === undefined
      ? InputProps
      : { ...InputProps, readOnly, className: inputClassName };

  useEffect(() => {
    const label = children.find(
      (item) => String((item as ItemType).id) === String(value)
    )?.label;

    if (name && value !== undefined && label && lookupTracker) {
      lookupTracker.register({
        name,
        id: value,
        label,
      });
    }
  }, [value, children]);

  const label =
    readOnly === undefined || readOnly === false
      ? props.label
      : `${props.label} (Read Only)`;

  return (
    <TextField
      select
      name={name}
      id={knownId}
      variant="outlined"
      fullWidth
      {...coreProps}
      value={safeValue}
      onChange={localOnChange}
      InputProps={inputProps}
      label={label}
    >
      {menuItems}
    </TextField>
  );
};
